/**
 * Maps cover letter form field names → template placeholder aliases.
 * 
 * Cover letter templates use TWO schemas:
 * - Schema A (newer, templates 6-8): use form field names directly
 *   e.g. {{fullName}}, {{companyMotivation}}, {{primarySkills}}
 * - Schema B (legacy, templates 1-5): use aliased names
 *   e.g. {{name}}, {{companyFocus}}, {{primarySkill}}, {{paragraph1/2/3}}
 *
 * This function ensures EVERY template finds the value it expects
 * without modifying template HTML.
 *
 * Shared between CoverLetterEditor (live editing) and
 * CoverLetterTemplatesGallery (preview rendering).
 *
 * @param {object} data - Cover letter form data (or sample data)
 * @returns {object} Enriched data with all aliases populated
 */
export const buildCoverLetterTemplateData = (data) => {
  const firstInitial = (data.fullName || '').charAt(0) || '';
  const lastInitial  = ((data.fullName || '').split(' ').pop() || '').charAt(0) || '';

  return {
    // ── Pass through all form fields as-is ──
    ...data,

    // ── Aliases for legacy CL templates (1-5) ──
    name:               data.fullName             || '',
    date:               data.currentDate           || '',
    hiringManager:      data.hiringManagerName     || '',
    address:            data.location              || '',
    closing:            'Sincerely,',

    // Gold-2 / Gold-3 paragraph-style placeholders
    paragraph1: data.jobTitle
      ? `I am writing to express my interest in the ${data.jobTitle} position at ${data.companyName || 'your organization'}. With ${data.yearsOfExperience || 'several'} years of experience in ${data.primarySkills || 'my field'}, I am confident in my ability to contribute effectively to your team.`
      : '',
    paragraph2: data.currentCompany
      ? `In my current role as ${data.currentRole || 'a professional'} at ${data.currentCompany}, I have ${data.keyAchievementOne || 'delivered impactful results'}. My expertise in ${data.toolsAndTechnologies || 'industry-standard technologies'} has enabled me to ${data.keyAchievementTwo || 'exceed expectations consistently'}.`
      : '',
    paragraph3: (data.companyMotivation || data.companyMission)
      ? `I am particularly drawn to ${data.companyName || 'your company'} because of ${data.companyMotivation || data.companyMission}. My background in ${data.relevantExperience || 'this domain'} positions me to make an immediate and meaningful contribution.`
      : '',

    // Common alternate placeholder names used by templates 2-5
    professionalTitle:   data.title                 || data.professionalTitle || '',
    experienceYears:     data.yearsOfExperience     || '',
    primarySkill:        data.primarySkills         || '',
    previousCompany:     data.currentCompany        || '',
    keyResponsibilities: data.keyAchievementTwo     || '',
    secondarySkills:     data.toolsAndTechnologies  || '',
    keyAchievements:     [data.keyAchievementOne, data.keyAchievementTwo].filter(Boolean).join('. ') || '',
    companyFocus:        data.companyMotivation     || data.companyMission || '',
    hiringManagerTitle:  '',
    companyAddress:      '',

    // Salutation alias (templates use hiringManagerSalutation or hiringManagerName)
    salutation:          data.hiringManagerSalutation || 'Hiring Manager',

    // Platinum photo / initials
    photoURL:            data.photoURL || data.photoDataUri || '',
    profilePhotoUrl:     data.photoURL || data.photoDataUri || '',
    nameInitials:        `${firstInitial}${lastInitial}`,

    // Portfolio / GitHub — not in CL form but used by some templates
    portfolioUrl:        '',
    githubUrl:           '',

    // Signature alias
    signature:           data.fullName              || '',
  };
};
